<template>
  <div ref="pdfContent" class="pdf-content">
    <CCard>
      <CCardHeader class="no-print">
        <div class="float-right no-print">
          <CButton
            color="primary"
            variant="outline"
            size="sm"
            class="ml-1"
            @click="downloadPDF"
            :disabled="isGeneratingPDF"
          >
            <CIcon name="cil-cloud-download" class="mr-1" />
            {{ isGeneratingPDF ? 'Generating PDF…' : 'Download PDF' }}
          </CButton>
          <CButton color="primary" variant="outline" size="sm" class="ml-1" @click="printContent">
            <CIcon name="cil-print" class="mr-1" /> Print
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <CRow class="justify-content-between align-items-center">
          <CCol>
            <img
              :src="getImageUrl()"
              alt="Business logo"
              class="img-fluid logo-image"
            />
          </CCol>
          <CCol class="text-right">
            <h1>PURCHASE ORDER</h1>
            <h5>{{ document.business.name }}</h5>
            <p>{{ "(" + document.business.regNo + ")" }}</p>
            <p v-html="formatAddress(document.business.address)"></p>
            <!--item.description is rendered from handlekeydown event and rerun format-->
            <p>Tel: {{ document.business.phone }}</p>
          </CCol>
        </CRow>
        <hr class="thick-hr" />
        <br />
        <div class="invoice-details">
          <CRow>
            <CCol sm="6">
              <p>
                <strong>{{ getCustomerName() }}</strong>
              </p>
              <p v-html="formatAddress(getCustomerAddress())"></p>
            </CCol>
            <CCol sm="6" class="text-right">
              <dl class="row">
                <dt class="col-sm-6">PO No:</dt>
                <dd class="col-sm-6">{{ document.orderNumber }}</dd>
                <dt class="col-sm-6">Issue Date:</dt>
                <dd class="col-sm-6">{{ getQuotationIssuedDate() }}</dd>
                <dt class="col-sm-6">Expected Date:</dt>
                <dd class="col-sm-6">{{ getQuotationExpiryDate() }}</dd>
                <dt class="col-sm-6">Reference:</dt>
                <dd class="col-sm-6">{{ getReferenceNo() }}</dd>
              </dl>
            </CCol>
          </CRow>
          <CRow v-if="document.title">
            <CCol>
              <p><strong>Title: </strong> {{ document.title }}</p>
            </CCol>
          </CRow>
        </div>

        <div class="table-wrapper">
          <table class="table table-striped">
            <thead>
              <tr>
                <th class="index-column">#</th>
                <th class="item-column">Item & Description</th>
                <th class="text-center quantity-column">Quantity</th>
                <th class="text-right price-column">
                  Price (RM)
                </th>
                <th class="text-right total-column">
                  Total (RM)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in computedQuotationItems" :key="item.name">
                <td class="index-column">{{ item.position }}</td>
                <td class="item-column">
                  <p>
                    <strong>{{ item.purchaseProduct.name }}</strong>
                  </p>
                  <p v-html="formatDescription(item.description)"></p>
                </td>
                <td class="text-center quantity-column">{{ item.quantity }}</td>
                <td class="text-right price-column">
                  {{ formatMyr(item.price) }}
                </td>
                <td class="text-right total-column">
                  {{ formatMyr(item.totalAmountPerItem) }}
                </td>
              </tr>

              <!-- Grand Total Row -->
              <tr>
                <td colspan="4" class="text-right">
                  <strong>Total</strong>
                </td>
                <td class="text-right">
                  <strong>RM {{ formatMyr(grandTotal) }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Terms and Conditions Section -->
        <div v-if="document.note" class="terms-and-conditions mt-4">
          <p><strong>Terms and Conditions</strong></p>
          <p v-html="formatNote(document.note)"></p>
        </div>

        <div class="report-footer">
          <p>
            {{ document.business.name }} | {{ document.business.website }} |
            {{ document.business.phone }}
          </p>
          <p>
            Powered By 
            <img
            
              src="/logo.png"
              alt="Niaga-ku Logo"
              class="footer-logo"
            />  Niaga-Ku.com
          </p>
        
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import html2pdf from "html2pdf.js";

export default {
  name: "WidgetsReportPurchaseOrder",
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isGeneratingPDF: false,
    };
  },
  computed: {
    computedQuotationItems() {
      return this.document.items.map((item) => {
        return {
          ...item,
          productName: item.purchaseProduct.name,
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },
    grandTotal() {
      return this.computedQuotationItems
        .reduce((acc, item) => {
          return acc + (Number(item.totalAmountPerItem) || 0);
        }, 0);
    },
    isMobile() {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
  },
  mounted() {
    this.forceA4Size();
  },
  methods: {
    formatMyr,
    formatNote(note) {
      return note.replace(/\n/g, "<br />");
    },

    formatDescription(description) {
      // split item.description \n
      return description
        .split("\n") //split into an array
        .map((line) => {
          // check for indentation (e.g., spaces or tabs at the start)
          const indentLevel = (line.match(/^(\s+)/) || [""])[0].length / 4;
          return `<p style="padding-left: ${
            indentLevel * 20
          }px;">${line.trim()}</p>`;
          //wraps in one line and padding for neatness
        })
        .join(""); //join into single html string and pass to <p>
    },

    getTotalItemPrice(item) {
      try {
        return item.quantity * item.price;
      } catch (error) {
        return 0;
      }
    },
    getQuotationIssuedDate() {
      return formatDate(this.document.issuedDate);
    },
    getReferenceNo() {
      try {
        return this.document.reference;
      } catch (error) {
        return "N/A";
      }
    },
    getQuotationExpiryDate() {
      return formatDate(this.document.expiryDate);
    },
    getCustomerAddress() {
      try {
        return this.document.vendor.address;
      } catch (error) {
        return "N/A";
      }
    },
    getCustomerName() {
      try {
        return this.document.vendor.name;
      } catch (error) {
        return "N/A";
      }
    },
    removeTrailingSlash(str) {
      return str.endsWith("/") ? str.slice(0, -1) : str;
    },

    getImageUrl() {
      try {
        return (
          this.removeTrailingSlash(window.apiUrl) + this.document.business.logoUrl
        );
      } catch (error) {
        return "";
      }
    },
    formatAddress(address) {
      return address.replace(/\n/g, "<br />");
    },
    forceA4Size() {
      const pdfContent = this.$refs.pdfContent;
      if (pdfContent) {
        // Ensure the size is recalculated and displayed correctly
        pdfContent.style.width = "210mm";
        pdfContent.style.height = "297mm";
      }
    },
    printContent() {
      // Mobile users get PDF download instead
      if (this.isMobile) {
        this.downloadPDF();
        return;
      }

      // Desktop users get new window print (preserves Vue app)
      this.openPrintWindow();
    },

    openPrintWindow() {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      const printContent = this.$refs.pdfContent.innerHTML;

      const printStyles = this.getPrintStyles();

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${this.document.business.shortName || 'Purchase Order'}_${this.document.orderNumber || 'PO'}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              ${printStyles}
            </style>
          </head>
          <body>
            <div class="pdf-content">
              ${printContent}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    },

    async downloadPDF() {
      this.isGeneratingPDF = true;

      try {
        const opt = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: `${this.document.business.shortName || 'PO'}_${this.document.orderNumber || 'PurchaseOrder'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: 210 * 3.78, // 210mm in pixels at 96 DPI
            height: 297 * 3.78, // 297mm in pixels at 96 DPI
            scrollX: 0,
            scrollY: 0
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'portrait'
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        // Clone the content and remove no-print elements
        const element = this.$refs.pdfContent.cloneNode(true);
        const noPrintElements = element.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());

        // Apply PDF-specific styling to override mobile styles
        element.style.width = '794px'; // A4 width in pixels
        element.style.minHeight = 'auto';
        element.style.padding = '20px';
        element.style.fontSize = '14px';
        element.style.lineHeight = '1.4';
        element.style.fontFamily = 'Arial, sans-serif';
        element.style.backgroundColor = '#ffffff';

        // Add PDF generation class to override mobile styles
        element.classList.add('pdf-generation');

        // Override mobile styles for consistent PDF output
        const logo = element.querySelector('.logo-image');
        if (logo) {
          logo.style.maxWidth = '180px';
          logo.style.height = 'auto';
          logo.style.display = 'block';
        }

        // Ensure table has proper sizing for PDF
        const table = element.querySelector('.table');
        if (table) {
          table.style.width = '100%';
          table.style.fontSize = '13px';
          table.style.minWidth = 'auto';
        }

        // Fix table cells
        const tableCells = element.querySelectorAll('.table th, .table td');
        tableCells.forEach(cell => {
          cell.style.padding = '8px 12px';
          cell.style.fontSize = '13px';
          cell.style.whiteSpace = 'normal';
        });

        // Fix column widths
        const indexCols = element.querySelectorAll('.index-column');
        indexCols.forEach(col => col.style.width = '50px');

        const quantityCols = element.querySelectorAll('.quantity-column');
        quantityCols.forEach(col => col.style.width = '85px');

        const priceCols = element.querySelectorAll('.price-column, .total-column');
        priceCols.forEach(col => col.style.width = '150px');

        const itemCols = element.querySelectorAll('.item-column');
        itemCols.forEach(col => {
          col.style.width = '250px';
          col.style.minWidth = 'auto';
        });

        // Fix typography
        const h1 = element.querySelector('h1');
        if (h1) {
          h1.style.fontSize = '28px';
          h1.style.margin = '0 0 10px 0';
        }

        const h5 = element.querySelector('h5');
        if (h5) {
          h5.style.fontSize = '16px';
          h5.style.margin = '0 0 5px 0';
        }

        // Fix row layouts for proper alignment
        const rows = element.querySelectorAll('.row');
        rows.forEach(row => {
          row.style.display = 'flex';
          row.style.flexWrap = 'wrap';
          row.style.margin = '0 -7.5px';
        });

        // Fix column layouts
        const cols = element.querySelectorAll('.col');
        cols.forEach(col => {
          col.style.flex = '1';
          col.style.padding = '0 7.5px';
        });

        const colSm6 = element.querySelectorAll('.col-sm-6');
        colSm6.forEach(col => {
          col.style.flex = '0 0 50%';
          col.style.maxWidth = '50%';
          col.style.padding = '0 7.5px';
        });

        // Fix alignment classes
        const justifyBetween = element.querySelectorAll('.justify-content-between');
        justifyBetween.forEach(el => {
          el.style.justifyContent = 'space-between';
        });

        const alignCenter = element.querySelectorAll('.align-items-center');
        alignCenter.forEach(el => {
          el.style.alignItems = 'center';
        });

        // Fix text alignment
        const textRight = element.querySelectorAll('.text-right');
        textRight.forEach(el => {
          el.style.textAlign = 'right';
        });

        const textLeft = element.querySelectorAll('.text-left');
        textLeft.forEach(el => {
          el.style.textAlign = 'left';
        });

        const textCenter = element.querySelectorAll('.text-center');
        textCenter.forEach(el => {
          el.style.textAlign = 'center';
        });

        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error('PDF generation failed:', error);
        // Fallback to print dialog
        this.openPrintWindow();
      } finally {
        this.isGeneratingPDF = false;
      }
    },

    getPrintStyles() {
      return `
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
          background: white;
        }

        .pdf-content {
          width: 794px;
          min-height: 1123px;
          margin: 0 auto;
          padding: 20px;
          background-color: white;
          font-size: 14px;
          line-height: 1.4;
          page-break-inside: avoid;
          font-family: Arial, sans-serif;
        }

        .no-print {
          display: none !important;
        }

        .logo-image {
          max-width: 100px;
          height: auto;
          display: block;
        }

        .row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -7.5px;
        }

        .col {
          flex: 1;
          padding: 0 7.5px;
        }

        .col-sm-6 {
          flex: 0 0 50%;
          max-width: 50%;
          padding: 0 7.5px;
        }

        .justify-content-between {
          justify-content: space-between;
        }

        .align-items-center {
          align-items: center;
        }

        .img-fluid {
          max-width: 100%;
          height: auto;
        }

        .thick-hr {
          border: 1px solid #000;
          margin: 10px 0;
        }

        .table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
          page-break-inside: avoid;
        }

        .table th,
        .table td {
          border: 1px solid #ddd;
          padding: 8px 12px;
          text-align: left;
          vertical-align: top;
          font-size: 13px;
        }

        .table th {
          background-color: #f5f5f5;
          font-weight: bold;
          page-break-after: avoid;
        }

        .table tr {
          page-break-inside: avoid;
        }

        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }

        .report-footer {
          text-align: center;
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #ccc;
          font-size: 10px;
          page-break-inside: avoid;
        }

        .footer-logo {
          max-width: 18px;
          height: auto;
          vertical-align: middle;
        }

        .document-header {
          margin-bottom: 15px;
          page-break-after: avoid;
        }

        .document-details {
          margin-bottom: 15px;
          page-break-after: avoid;
        }

        h1 {
          font-size: 28px;
          margin: 0 0 10px 0;
          page-break-after: avoid;
          font-weight: bold;
        }

        h5 {
          font-size: 16px;
          margin: 0 0 5px 0;
          font-weight: bold;
        }

        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          .pdf-content {
            width: 100%;
            margin: 0;
            padding: 15mm;
            box-sizing: border-box;
          }
          .table {
            font-size: 12px;
          }
          .table th,
          .table td {
            padding: 6px 8px;
            font-size: 11px;
          }
        }
      `;
    },
  },
};
</script>

<style scoped>

.report-footer {
  text-align: center;
  margin-top: 30px;
  padding-top: 15px;
  border-top: 1px solid #ccc;
  page-break-inside: avoid;
}

.report-footer p {
  margin: 5px 0;
  font-size: 14px;
  color: #333;
}

.footer-logo {
  max-width: 20px;
  height: auto;
  filter: grayscale(100%);
}


.logo-image {
  max-width: 180px;
  height: auto;
}
.pdf-content > .CRow:first-child {
  margin-bottom: 0; /* Reduce space between header and content */
}
.pdf-content {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  background-color: white;
  font-size: 14px;
  line-height: 1.4;
  font-family: Arial, sans-serif;
}

.table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

.table {
  width: 100%;
  table-layout: fixed;
  word-wrap: break-word;
  page-break-inside: auto;
}

.table tr {
  page-break-inside: auto; /* Prevent page breaks inside table rows */
  page-break-after: auto; /* Allow page breaks after rows */
}

.quantity-column {
  width: 85px;
  text-align: center;
}

.price-column {
  width: 150px;
  text-align: center;
}

.total-column {
  width: 150px;
  text-align: center;
}

.item-column {
  width: 250px;
}

.index-column {
  width: 50px;
  text-align: center;
}

.table td,
.table th {
  white-space: normal; /* Ensure text wraps within cells */
}

/* PDF Generation Override - ensures consistent PDF output regardless of device */
.pdf-generation {
  width: 794px !important;
  padding: 20px !important;
  font-size: 14px !important;
}

.pdf-generation .logo-image {
  max-width: 180px !important;
}

.pdf-generation .table {
  width: 100% !important;
  min-width: auto !important;
  font-size: 13px !important;
}

.pdf-generation .table th,
.pdf-generation .table td {
  padding: 8px 12px !important;
  font-size: 13px !important;
  white-space: normal !important;
}

.pdf-generation .index-column {
  width: 50px !important;
}

.pdf-generation .quantity-column {
  width: 85px !important;
}

.pdf-generation .price-column,
.pdf-generation .total-column {
  width: 150px !important;
}

.pdf-generation .item-column {
  width: 250px !important;
  min-width: auto !important;
}

.pdf-generation h1 {
  font-size: 28px !important;
  margin: 0 0 10px 0 !important;
}

.pdf-generation h5 {
  font-size: 16px !important;
  margin: 0 0 5px 0 !important;
}

.pdf-generation .row {
  display: flex !important;
  flex-wrap: wrap !important;
  margin: 0 -7.5px !important;
}

.pdf-generation .col {
  flex: 1 !important;
  padding: 0 7.5px !important;
}

.pdf-generation .col-sm-6 {
  flex: 0 0 50% !important;
  max-width: 50% !important;
  padding: 0 7.5px !important;
}

.pdf-generation .justify-content-between {
  justify-content: space-between !important;
}

.pdf-generation .align-items-center {
  align-items: center !important;
}

.pdf-generation .text-right {
  text-align: right !important;
}

.pdf-generation .text-left {
  text-align: left !important;
}

.pdf-generation .text-center {
  text-align: center !important;
}

@media screen and (max-width: 768px) {
  .pdf-content {
    width: 100%;
    height: auto;
    padding: 15px;
    box-sizing: border-box;
  }

  .logo-image {
    max-width: 120px;
    height: auto;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table {
    min-width: 600px;
    font-size: 12px;
  }

  .table th,
  .table td {
    padding: 8px 6px;
    font-size: 11px;
    white-space: nowrap;
  }

  .item-column {
    min-width: 200px;
    white-space: normal;
  }

  .index-column {
    width: 40px;
  }

  .quantity-column {
    width: 70px;
  }

  .price-column,
  .total-column {
    width: 90px;
  }

  h1 {
    font-size: 20px;
    margin: 0 0 8px 0;
  }

  h5 {
    font-size: 14px;
    margin: 0 0 4px 0;
  }

  .invoice-details {
    margin: 15px 0;
  }

  .report-footer {
    margin-top: 20px;
    font-size: 12px;
  }

  .footer-logo {
    max-width: 16px;
  }
}

@media screen and (max-width: 480px) {
  .pdf-content {
    padding: 10px;
    font-size: 12px;
  }

  .logo-image {
    max-width: 100px;
  }

  h1 {
    font-size: 18px;
  }

  h5 {
    font-size: 12px;
  }

  .table {
    font-size: 10px;
  }

  .table th,
  .table td {
    padding: 6px 4px;
    font-size: 10px;
  }

  .item-column {
    min-width: 150px;
  }

  .quantity-column,
  .price-column,
  .total-column {
    width: 70px;
  }
}

@media print {
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
  }

  .pdf-content {
    width: 100%;
    height: auto;
    margin: 0;
    padding: 15mm;
    box-sizing: border-box;
    page-break-inside: avoid;
    background: white;
  }

  .table-wrapper {
    page-break-before: auto;
    margin-top: 15px;
  }

  .invoice-details {
    margin: 15px 0;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .table th,
  .table td {
    padding: 6px 8px;
    font-size: 11px;
    border: 1px solid #ddd;
  }

  .table tr {
    page-break-after: auto;
  }

  h1 {
    font-size: 24px;
    margin: 0 0 10px 0;
  }

  h5 {
    font-size: 14px;
    margin: 0 0 5px 0;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .logo-image {
    max-width: 100px;
    height: auto;
    display: block;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7.5px;
  }

  .col {
    flex: 1;
    padding: 0 7.5px;
  }

  .col-sm-6 {
    flex: 0 0 50%;
    max-width: 50%;
    padding: 0 7.5px;
  }

  .justify-content-between {
    justify-content: space-between;
  }

  .align-items-center {
    align-items: center;
  }

  .img-fluid {
    max-width: 100%;
    height: auto;
  }

  .no-print {
    display: none !important;
  }
}

.text-right {
  text-align: right;
}

.thick-hr {
  border: 1px solid #000;
  margin: 10px 0;
}

.terms-and-conditions {
  margin-top: 20px;
}

@media print {
  .no-print {
    display: none !important;
  }
}
</style>
